import { useState } from 'react';
import { Mail, Linkedin, Calendar, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Create FormData for the API request
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.company ? `Message from ${formData.company}` : 'Portfolio Contact Form');
      formDataToSend.append('message', formData.message);
      
      // Send to backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        body: formDataToSend
      });
      
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      const result = await response.json();
      
      setIsSubmitted(true);
      setIsLoading(false);
      toast.success(result.message || 'Message sent successfully! I\'ll respond within 24 hours.');
      
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', company: '', message: '' });
      }, 4000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setIsLoading(false);
      toast.error('Failed to send message. Please try again or email me directly at aakashvrma05@gmail.com');
    }
  };

  return (
    <div className="netflix-row" id="contact">
      <h2 className="netflix-row-title">Get In Touch</h2>
      <div className="netflix-row-container">
        <div className="grid md:grid-cols-2 gap-8 px-4">
          {/* Contact Information */}
          <div className="space-y-6">
            <div className="netflix-card p-6">
              <h3 className="text-xl font-semibold mb-4 text-white">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-netflix-red" />
                  <div>
                    <div className="text-white font-medium">Email</div>
                    <a href="mailto:aakashvrma05@gmail.com" className="text-netflix-gray-400 hover:text-white transition-colors">
                      aakashvrma05@gmail.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Linkedin className="w-5 h-5 text-netflix-red" />
                  <div>
                    <div className="text-white font-medium">LinkedIn</div>
                    <a 
                      href="https://www.linkedin.com/in/akashvermaufl" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-netflix-gray-400 hover:text-white transition-colors"
                    >
                      linkedin.com/in/akashvermaufl
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-netflix-red" />
                  <div>
                    <div className="text-white font-medium">Availability</div>
                    <div className="text-netflix-gray-400">Available for Strategic Leadership Roles</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-netflix-red" />
                  <div>
                    <div className="text-white font-medium">Location</div>
                    <div className="text-netflix-gray-400">Phoenix, AZ</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="netflix-card p-6">
              <h4 className="text-lg font-semibold mb-3 text-white">Discussion Areas</h4>
              <div className="space-y-2">
                {[
                  "Strategic Risk Management",
                  "Cloud Security Governance", 
                  "AI Safety & Compliance",
                  "Regulatory Framework Implementation",
                  "Enterprise Security Transformation"
                ].map((area, index) => (
                  <div key={index} className="netflix-badge netflix-badge-secondary text-xs">
                    {area}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="netflix-card p-6">
            <h3 className="text-xl font-semibold mb-4 text-white">Send a Message</h3>
            
            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-netflix-gray-200 border border-netflix-gray-300 rounded text-white placeholder-netflix-gray-400 focus:outline-none focus:border-netflix-red transition-colors"
                  />
                </div>
                
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-netflix-gray-200 border border-netflix-gray-300 rounded text-white placeholder-netflix-gray-400 focus:outline-none focus:border-netflix-red transition-colors"
                  />
                </div>
                
                <div>
                  <input
                    type="text"
                    name="company"
                    placeholder="Company (Optional)"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-netflix-gray-200 border border-netflix-gray-300 rounded text-white placeholder-netflix-gray-400 focus:outline-none focus:border-netflix-red transition-colors"
                  />
                </div>
                
                <div>
                  <textarea
                    name="message"
                    placeholder="Tell me about your strategic risk challenges or leadership opportunities..."
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-netflix-gray-200 border border-netflix-gray-300 rounded text-white placeholder-netflix-gray-400 focus:outline-none focus:border-netflix-red transition-colors resize-none"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full netflix-btn-play flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <span>Send Message</span>
                  )}
                </button>
              </form>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Message Sent!</h4>
                <p className="text-netflix-gray-400">
                  Thank you for reaching out. I'll respond within 24 hours with insights tailored to your needs.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};